import { combineReducers } from 'redux'
import * as types from 'models/actionTypes'
import * as RenderItemTypes from 'constants/renderItemTypes'

export const initialState = {
    selectedId: -1,
    selectedRenderItemId: -1,
    draggingRenderItemId: -1,
    nextRenderItemId: 0,
    byId: {
        '-1': {
            id: -1,
            wid: -1,
            renderItems: {},
            sceneId: null,
            name: '',
            thumbUrl: '',
            uuid: '',
            isShared: false,
            isStock: false,
            geoProperties: {},
            savedProperties: {},
            wallPaint: {},
            hiddenSurfaces: [],
        },
    },
    isSaving: false,
    // TODO: isModified should be per-composition, ideally
    isModified: false,
    isLoadingCompositions: false,
    isLoadingCompiledComposition: false,
    hasSaveError: false,
    hoverRenderType: null,
    nextStackableItem: {
        settings: {},
        trackingInfo: {},
        waitingInteraction: false,
    },
    swappingSurfaces: false,
    userDidAddRenderItem: false,
}

// When creating new render items in the store
// these are the properties that are unsaved
// and must be initialized from scratch.
// Use for loading/placing/swapping/duplicating/etc
const resetableProps = {
    metrics: null,
    readyAndRendered: false,
}

const translateServComp = (composition, renderItems) => {
    if (!composition) {
        return {}
    }
    const placedProducts = composition.placedfurnitureSet
    const ri = Object.assign(
        {},
        ...placedProducts.map((item) => {
            const { gfxAttributes } = item
            let artData = {}
            if (gfxAttributes && gfxAttributes.artData) {
                artData = gfxAttributes.artData
            }
            let uniqueId
            if (item.uniqueId) {
                uniqueId = parseInt(item.uniqueId, 10)
            } else {
                // Backfill uniqueId to load mobile compositions
                uniqueId = Date.now() - Math.round(Math.random() * 100000)
            }
            return {
                [uniqueId]: {
                    id: item.furnitureId,
                    uniqueId,
                    x: item.x,
                    y: item.y,
                    heading: item.heading,
                    stackedPlane: gfxAttributes && gfxAttributes.stackedPlane,
                    combinablePlacement: gfxAttributes && gfxAttributes.combinablePlacement,

                    ...artData,

                    ...resetableProps,
                },
            }
        }),
    )
    return {
        [composition.compositionId]: {
            id: composition.compositionId,
            wid: composition.wid,
            uuid: composition.uuid,
            isShared: composition.isShared,
            isStock: composition.isStock || false,
            renderItems: renderItems || ri || {},
            sceneId: composition.sceneId,
            name: composition.name,
            creationDate: composition.createdAt,
            thumbUrl: composition.thumbnailUrl || '',
            geoProperties: composition.geoProperties || {},
            savedProperties: composition.savedProperties || {},
            wallPaint: (composition.savedProperties && composition.savedProperties.wallPaint) || {},
            hiddenSurfaces: [],
        },
    }
}

export const byId = (state = initialState.byId, action) => {
    switch (action.type) {
        case types.RECEIVE_COMPOSITIONS:
            return Object.assign({}, ...action.compositions.map((comp) => translateServComp(comp)), state)
        case types.RECEIVE_COMPILED_COMPOSITION:
            return {
                ...translateServComp(action.composition),
                ...state, // Don't allow incoming comps to overwrite state
            }
        case types.LOAD_COMPOSITION_FROM_LOCAL_STORAGE:
            return {
                ...state,
                ...translateServComp(action.composition), // Local storage can overwrite state
            }
        case types.RECEIVE_SAVE_COMPOSITION:
            // TODO. For now just copy the original composition
            if (action.isNew) {
                return {
                    ...state,
                    ...translateServComp(action.composition, state[action.originId].renderItems),
                }
            }
            return {
                ...state,
                [action.composition.compositionId]: {
                    ...state[action.composition.compositionId],
                    thumbUrl: action.composition.thumbnailUrl || '',
                },
            }
        case types.PLACE_RENDER_ITEM:
            return {
                ...state,
                [action.compositionId]: {
                    ...state[action.compositionId],
                    renderItems: {
                        ...state[action.compositionId].renderItems,
                        [action.uniqueId]: {
                            id: action.id,
                            uniqueId: action.uniqueId,
                            x: action.x,
                            y: action.y,
                            ...action.settings,
                            ...resetableProps,
                        },
                    },
                },
            }
        case types.SWAP_RENDER_ITEM: {
            const renderItems = { ...state[action.compositionId].renderItems }
            const targetRenderItem = renderItems[action.targetRenderItemId]

            const swappedInRenderItem = {
                ...targetRenderItem,
                uniqueId: action.uniqueId,
                id: action.productId,
                ...resetableProps,
            }

            if (targetRenderItem.swapChainPosition !== undefined) {
                swappedInRenderItem.x = targetRenderItem.swapChainPosition[0]
                swappedInRenderItem.y = targetRenderItem.swapChainPosition[1]

                swappedInRenderItem.swapChainPosition = [...targetRenderItem.swapChainPosition]
            } else {
                swappedInRenderItem.swapChainPosition = [targetRenderItem.x, targetRenderItem.y]
            }

            delete renderItems[action.targetRenderItemId]

            renderItems[action.uniqueId] = swappedInRenderItem

            return {
                ...state,
                [action.compositionId]: {
                    ...state[action.compositionId],
                    renderItems,
                },
            }
        }
        case types.DELETE_COMPOSITION_RENDER_ITEM: {
            const { id: deletedId, compositionId } = action
            const renderItems = { ...state[compositionId].renderItems }
            delete renderItems[deletedId]

            // Process for stacked children
            Object.values(renderItems).forEach((renderItem) => {
                // If item stacks, check if stackedPlane begins with deletedId
                if (renderItem.stackedPlane && renderItem.stackedPlane.indexOf(deletedId) === 0) {
                    // Delete "children" stacked on deleted render item
                    delete renderItems[renderItem.uniqueId]
                }
            })

            return {
                ...state,
                [compositionId]: {
                    ...state[compositionId],
                    renderItems,
                },
            }
        }
        case types.SWAP_STACKABLE_SURFACE: {
            const renderItems = { ...state[action.compositionId].renderItems }
            renderItems[action.itemId].stackedPlane = action.planeId
            renderItems[action.itemId].x = action.x
            renderItems[action.itemId].y = action.y
            renderItems[action.itemId].swapChainPosition = renderItems[action.itemId].swapChainPosition && [
                action.x,
                action.y,
            ]
            return {
                ...state,
                [action.compositionId]: {
                    ...state[action.compositionId],
                    renderItems,
                },
            }
        }
        case types.SWAP_STACKABLE_TO_FLOOR: {
            // This reducer finds the target renderItem, removes
            // its .stackedPlane property, and sets a new (x, y)
            // position. This removes a stackable from a surface
            // and places it on the floor.

            const renderItems = {
                ...state[action.compositionId].renderItems,
            }

            const targetItem = {
                ...renderItems[action.itemId],
                x: action.x,
                y: action.y,
            }

            targetItem.swapChainPosition = targetItem.swapChainPosition && [action.x, action.y]
            delete targetItem.stackedPlane

            renderItems[action.itemId] = targetItem

            return {
                ...state,
                [action.compositionId]: {
                    ...state[action.compositionId],
                    renderItems,
                },
            }
        }
        case types.UPDATE_COMPOSITION_RENDER_ITEM:
        case types.RENDER_ITEM_LOADED:
        case types.WALLART_ITEM_LOADED: {
            const { compositionId, id: renderItemUniqueId, item } = action
            const { renderItems } = state[compositionId]
            // If the user is swapping really quickly, the RENDER_ITEM_LOADED comes in
            // after the item has been swapped. In this case, we do not want to want to
            // add metrics  or other item data for a renderitem that has been updated/removed.
            if (!renderItems[renderItemUniqueId]) {
                return state
            }

            return {
                ...state,
                [compositionId]: {
                    ...state[compositionId],
                    renderItems: {
                        ...renderItems,
                        [renderItemUniqueId]: {
                            ...renderItems[renderItemUniqueId],
                            ...item,
                        },
                    },
                },
            }
        }

        case types.CLEAR_SWAP_AFFINITY: {
            const newState = { ...state }
            const { compositionId, id } = action
            delete newState[compositionId].renderItems[id].swapChainPosition
            Object.values(newState[compositionId].renderItems)
                .filter((item) => item.stacks && item.stackedPlane.startsWith(id))
                .map((item) => item.uniqueId)
                .forEach((itemId) => {
                    delete newState[compositionId].renderItems[itemId].swapChainPosition
                })
            return newState
        }

        case types.RENAME_COMPOSITION:
            return {
                ...state,
                [action.id]: {
                    ...state[action.id],
                    name: action.name,
                },
            }
        case types.REQUEST_DELETE_COMPOSITION: {
            const nextState = { ...state }
            delete nextState[action.id]
            return nextState
        }
        case types.SELECT_SCENE:
            // Clear items from the default comp
            // when selecting scene (the selected id
            // also gets reset)
            return {
                ...state,
                '-1': {
                    ...initialState.byId['-1'],
                },
            }
        case types.SHARE_COMPOSITION: {
            if (state[action.id] && state[action.id].isShared) {
                return state
            }
            return {
                ...state,
                [action.id]: {
                    ...state[action.id],
                    isShared: true,
                },
            }
        }
        case types.UPDATE_COMPOSITION_SAVE_DATA:
            return {
                ...state,
                [action.compositionId]: {
                    ...state[action.compositionId],
                    savedProperties: {
                        ...state[action.compositionId].savedProperties,
                        ...action.saveData,
                    },
                },
            }
        case types.REMOVE_RENDER_ITEMS: {
            const { compositionId } = action
            const composition = state[compositionId]
            const toBeDeletedRenderItems = { ...composition.renderItems }
            return {
                ...state,
                [action.compositionId]: {
                    ...composition,
                    renderItems: {},
                    deletedRenderItems: toBeDeletedRenderItems, // to be used later for undo delete
                },
            }
        }
        case types.UNDO_REMOVE_RENDER_ITEMS: {
            const { compositionId } = action
            const composition = state[compositionId]
            return {
                ...state,
                [action.compositionId]: {
                    ...composition,
                    renderItems: { ...composition.deletedRenderItems },
                },
            }
        }

        case types.SET_WALL_COLOR: {
            const wallPaint = { ...state[action.compositionId].wallPaint }
            if (action.colorString) {
                wallPaint[action.planeIndex] = action.colorString
            } else {
                delete wallPaint[action.planeIndex]
            }

            return {
                ...state,
                [action.compositionId]: {
                    ...state[action.compositionId],
                    wallPaint,
                },
            }
        }
        case types.UPDATE_HIDDEN_SURFACES:
            return {
                ...state,
                [action.compositionId]: {
                    ...state[action.compositionId],
                    hiddenSurfaces: action.surfaces,
                },
            }

        default:
            return state
    }
}

export const selectedId = (state = initialState.selectedId, action) => {
    switch (action.type) {
        case types.SELECT_COMPOSITION:
            return action.compositionId
        case types.SELECT_SCENE:
            // Select the default comp when changing the scene
            return -1
        default:
            return state
    }
}

export const selectedRenderItemId = (state = initialState.selectedRenderItemId, action) => {
    switch (action.type) {
        case types.SWAP_RENDER_ITEM:
            return action.uniqueId
        case types.SELECT_COMPOSITION_RENDER_ITEM:
            return action.renderItemId
        case types.OPEN_DESIGN_SIDEPANEL:
            return action.renderItemId || state
        case types.DELETE_COMPOSITION_RENDER_ITEM:
            return -1
        default:
            return state
    }
}

export const draggingRenderItemId = (state = initialState.draggingRenderItemId, action) => {
    switch (action.type) {
        case types.START_DRAG_RENDER_ITEM:
            return action.uniqueId
        case types.STOP_DRAG_RENDER_ITEM:
            return -1
        default:
            return state
    }
}

export const nextRenderItemId = (state = initialState.nextRenderItemId, action) => {
    switch (action.type) {
        case types.SET_INITIAL_LOADING:
        case types.SELECT_ART:
        case types.SELECT_FURNITURE:
        case types.QUICK_PREVIEW_ART:
        case types.PLACE_RENDER_ITEM:
        case types.SWAP_RENDER_ITEM:
        case types.DUPLICATE_RENDER_ITEM:
            return Date.now()
        default:
            return state
    }
}

export const isSaving = (state = initialState.isSaving, action) => {
    switch (action.type) {
        case types.REQUEST_SAVE_COMPOSITION:
            return true
        case types.RECEIVE_SAVE_COMPOSITION:
        case types.FAILED_SAVE_COMPOSITION:
            return false
        default:
            return state
    }
}

export const hasSaveError = (state = initialState.hasSaveError, action) => {
    switch (action.type) {
        case types.REQUEST_SAVE_COMPOSITION:
        case types.RECEIVE_SAVE_COMPOSITION:
            return false

        case types.FAILED_SAVE_COMPOSITION:
            return true
        default:
            return state
    }
}

export const isModified = (state = initialState.isModified, action) => {
    // TODO: setting to false when you select scene isn't true
    switch (action.type) {
        case types.PLACE_RENDER_ITEM:
        case types.UPDATE_COMPOSITION_RENDER_ITEM:
        case types.RENAME_COMPOSITION:
        case types.UPDATE_COMPOSITION_SAVE_DATA:
        case types.SWAP_RENDER_ITEM:
            return true
        case types.SELECT_COMPOSITION:
        case types.SELECT_SCENE:
        case types.RECEIVE_SAVE_COMPOSITION:
            return false
        case types.DELETE_COMPOSITION_RENDER_ITEM:
            if (action.compositionId === -1 && action.isLastItem) {
                // Empty default comp is not modified
                return false
            }
            return true
        default:
            return state
    }
}

const isLoadingCompositions = (state = initialState.isLoadingCompositions, action) => {
    switch (action.type) {
        case types.REQUEST_COMPOSITIONS_SAGA:
            return true
        case types.RECEIVE_COMPOSITIONS:
        case types.FAILED_RECEIVE_COMPOSITIONS:
            return false
        default:
            return state
    }
}

const isLoadingCompiledComposition = (state = initialState.isLoadingCompiledComposition, action) => {
    switch (action.type) {
        case types.REQUEST_COMPILED_COMPOSITION_SAGA:
            return true
        case types.RECEIVE_COMPILED_COMPOSITION:
        case types.FAILED_RECEIVE_COMPILED_COMPOSITION:
            return false
        case types.SET_COMPOSITION_PRE_FETCH_LOADING:
            return action.isLoading
        default:
            return state
    }
}

export const hoverRenderType = (state = initialState.hoverRenderType, action) => {
    switch (action.type) {
        case types.SELECT_ART:
            return RenderItemTypes.ART_ITEM
        case types.SELECT_FURNITURE:
            return RenderItemTypes.FURNITURE_ITEM
        default:
            return state
    }
}

export const nextStackableItem = (state = initialState.nextStackableItem, action) => {
    switch (action.type) {
        case types.START_PLACE_STACKABLE_ITEM:
            return {
                settings: action.settings,
                trackingInfo: action.trackingInfo,
                waitingInteraction: true,
            }
        case types.HIDE_PLACEABLE_SURFACES:
            return {
                settings: {},
                trackingInfo: {},
                waitingInteraction: false,
            }
        default:
            return state
    }
}

const swappingSurfaces = (state = initialState.swappingSurfaces, action) => {
    switch (action.type) {
        case types.START_SWAP_STACKABLE_SURFACE:
            return true
        case types.HIDE_PLACEABLE_SURFACES:
            return false
        default:
            return state
    }
}

const userDidAddRenderItem = (state = initialState.userDidAddRenderItem, action) => {
    switch (action.type) {
        case types.SET_INITIAL_LOADING:
            return false
        case types.PLACE_RENDER_ITEM:
            return true
        default:
            return state
    }
}

export default combineReducers({
    byId,
    selectedId,
    selectedRenderItemId,
    draggingRenderItemId,
    nextRenderItemId,
    isSaving,
    isModified,
    isLoadingCompositions,
    isLoadingCompiledComposition,
    hasSaveError,
    hoverRenderType,
    nextStackableItem,
    swappingSurfaces,
    userDidAddRenderItem,
})
